import { ChartDataPoint, DataLayer } from 'features/systemLens'
import { DEFAULT_CHART_SETTINGS } from 'features/timeSeries/config/constants'
import { getSensorColor } from 'features/timeSeries/config/timeSeriesColors'
import {
  predictValues,
  RegressionResult,
} from 'features/timeSeries/regressionHelpers'
import {
  EnhancedSensorEntry,
  RegressionConfig,
  SensorAxisMapping,
  TimeSeriesDerivedValueEntryJSON,
} from 'types'
import uPlot from 'uplot'

import { resampleData } from './dataProcessing'

interface UPlotDataOptions {
  dataLayers: DataLayer[]
  chartData: ChartDataPoint[]
  allSensorsToRender: Array<{ id: string; name: string }>
  enhancedSensorEntries: EnhancedSensorEntry[]
  unitMapping: Record<string, string>
  sensorAxisMapping: SensorAxisMapping
  regressionConfigs: RegressionConfig[]
  regressionResults: Record<string, RegressionResult>
  derivedValues: TimeSeriesDerivedValueEntryJSON[]
  xAxisSensorId?: string | null
  connectPoints: boolean
  sensorVisibilityConfig: Record<string, boolean>
  customSensorColors?: Record<string, string>
  highlightedSensorId?: string | null
  secondaryHighlightedSensorIds?: string[]
  annotationProtectedSensorIds?: string[]
  /** Sensors rendered de-emphasized (dotted + translucent), e.g. related ones. */
  deemphasizedSensorIds?: string[]
  singleScaleMode: boolean
}

/**
 * Extended uPlot series config with sensor metadata.
 * Properties from uPlot.Series are explicitly redeclared because tsgo
 * may not resolve the uPlot namespace types when using `extends`.
 */
export interface UPlotSeriesConfig extends uPlot.Series {
  label?: string | HTMLElement
  scale?: string
  show?: boolean
  stroke?: string
  fill?: string
  width?: number
  alpha?: number
  spanGaps?: boolean
  dash?: number[]
  points?: { show?: boolean; size?: number; fill?: string }
  _sensorId?: string
  _isRegression?: boolean
}

interface AlignedColumnsOptions {
  sensorsToPlot: Array<{ id: string; name: string }>
  sortedX: number[]
  sensorLookups: Map<string, Map<number, number | null>>
  sensorVisibilityConfig: Record<string, boolean>
  customSensorColors?: Record<string, string>
  highlightedSensorId?: string | null
  secondaryHighlightedSensorIds?: string[]
  annotationProtectedSensorIds?: string[]
  deemphasizedSensorIds?: string[]
  connectPoints: boolean
  enhancedSensorEntries: EnhancedSensorEntry[]
  sensorAxisMapping: SensorAxisMapping
  singleScaleMode: boolean
}

function buildSensorValueMaps(
  sensorsToPlot: Array<{ id: string }>,
  chartData: ChartDataPoint[],
  xAxisSensorId: string | null | undefined
): {
  sensorXValues: Map<string, (number | null)[]>
  sensorYValues: Map<string, (number | null)[]>
} {
  const sensorXValues = new Map<string, (number | null)[]>()
  const sensorYValues = new Map<string, (number | null)[]>()

  sensorsToPlot.forEach(({ id: sensorId }) => {
    const xVals: (number | null)[] = []
    const yVals: (number | null)[] = []

    if (xAxisSensorId) {
      chartData.forEach((point) => {
        const xValue = point[xAxisSensorId]
        const yValue = point[sensorId]
        if (typeof xValue === 'number' && typeof yValue === 'number') {
          xVals.push(xValue)
          yVals.push(yValue)
        }
      })
    } else {
      chartData.forEach((point) => {
        if (
          point.timestamp !== null &&
          point.timestamp !== undefined &&
          typeof point[sensorId] === 'number'
        ) {
          xVals.push(point.timestamp)
          yVals.push(point[sensorId] as number)
        }
      })
    }

    const { x: resampledX, y: resampledY } = resampleData(
      xVals,
      yVals,
      DEFAULT_CHART_SETTINGS.RESAMPLE_MAX_POINTS
    )

    sensorXValues.set(sensorId, resampledX)
    sensorYValues.set(sensorId, resampledY)
  })

  return { sensorXValues, sensorYValues }
}

function buildUnifiedXAxis(
  sensorsToPlot: Array<{ id: string }>,
  sensorXValues: Map<string, (number | null)[]>
): number[] {
  const xSet = new Set<number>()

  sensorsToPlot.forEach(({ id: sensorId }) => {
    const xs = sensorXValues.get(sensorId) || []
    xs.forEach((x) => {
      if (x !== null) xSet.add(x)
    })
  })

  return Array.from(xSet).sort((a, b) => a - b)
}

function buildSensorLookups(
  sensorsToPlot: Array<{ id: string }>,
  sensorXValues: Map<string, (number | null)[]>,
  sensorYValues: Map<string, (number | null)[]>
): Map<string, Map<number, number | null>> {
  const sensorLookups = new Map<string, Map<number, number | null>>()

  sensorsToPlot.forEach(({ id: sensorId }) => {
    const xs = sensorXValues.get(sensorId) || []
    const ys = sensorYValues.get(sensorId) || []
    const lookup = new Map<number, number | null>()
    xs.forEach((x, i) => {
      if (x !== null) lookup.set(x, ys[i])
    })
    sensorLookups.set(sensorId, lookup)
  })

  return sensorLookups
}

function buildAlignedColumns(options: AlignedColumnsOptions): {
  yColumns: (number | null | undefined)[][]
  seriesAdditions: UPlotSeriesConfig[]
} {
  const {
    sensorsToPlot,
    sortedX,
    sensorLookups,
    sensorVisibilityConfig,
    customSensorColors,
    highlightedSensorId,
    secondaryHighlightedSensorIds,
    annotationProtectedSensorIds,
    deemphasizedSensorIds,
    connectPoints,
    enhancedSensorEntries,
    sensorAxisMapping,
    singleScaleMode,
  } = options

  const deemphasizedSet = new Set(deemphasizedSensorIds ?? [])

  const hasHighlight =
    highlightedSensorId !== null ||
    (secondaryHighlightedSensorIds?.length ?? 0) > 0
  const yColumns: (number | null | undefined)[][] = []
  const seriesAdditions: UPlotSeriesConfig[] = []

  sensorsToPlot.forEach(({ id: sensorId, name }, index) => {
    const lookup = sensorLookups.get(sensorId)!
    const yCol: (number | null | undefined)[] = sortedX.map(
      (x) => lookup.get(x) ?? null
    )
    yColumns.push(yCol)

    const visible = sensorVisibilityConfig?.[sensorId] ?? true
    const lineColor = customSensorColors?.[sensorId] || getSensorColor(index)
    const isHighlighted = highlightedSensorId === sensorId
    const isSecondaryHighlighted =
      secondaryHighlightedSensorIds?.includes(sensorId) ?? false
    const isAnnotationProtected =
      annotationProtectedSensorIds?.includes(sensorId) ?? false
    const isDeemphasized = deemphasizedSet.has(sensorId)
    const opacity = hasHighlight
      ? isHighlighted
        ? 1
        : isSecondaryHighlighted
          ? 0.72
          : isAnnotationProtected
            ? 1
            : 0.24
      : isDeemphasized
        ? 0.4
        : 1
    const lineWidth = connectPoints
      ? isHighlighted
        ? 1.9
        : isSecondaryHighlighted
          ? 1.45
          : 1.2
      : 0
    const markerSize = connectPoints ? 0 : isHighlighted ? 6 : 4

    const isDerived =
      sensorId.startsWith('derived_') || sensorId.startsWith('regression_')
    let displayName: string
    if (isDerived) {
      displayName = name || sensorId
    } else {
      const sensor = enhancedSensorEntries.find((e) => e.id === sensorId)
      displayName = sensor?.data?.name || sensorId
    }

    const scaleKey = singleScaleMode
      ? 'y'
      : sensorAxisMapping[sensorId]
        ? sensorId
        : 'y'

    seriesAdditions.push({
      label: displayName,
      scale: scaleKey,
      show: visible,
      stroke: lineColor,
      width: lineWidth,
      alpha: opacity,
      spanGaps: true,
      ...(isDeemphasized ? { dash: [2, 4] } : {}),
      points: {
        show: !connectPoints,
        size: markerSize,
        fill: lineColor,
      },
      _sensorId: sensorId,
    })
  })

  return { yColumns, seriesAdditions }
}

/**
 * Builds a unified timestamp array and aligned y-columns from ChartDataPoint[]
 */
export function generateUPlotData(options: UPlotDataOptions): {
  data: uPlot.AlignedData
  series: UPlotSeriesConfig[]
} {
  const {
    dataLayers,
    chartData,
    allSensorsToRender,
    enhancedSensorEntries,
    sensorAxisMapping,
    xAxisSensorId,
    connectPoints,
    sensorVisibilityConfig,
    customSensorColors,
    highlightedSensorId,
    secondaryHighlightedSensorIds,
    annotationProtectedSensorIds,
    deemphasizedSensorIds,
    singleScaleMode,
    regressionConfigs,
    regressionResults,
    derivedValues,
  } = options

  const processDataLayer = dataLayers.find((l) => l.id === 'process-data')
  const seriesConfigs: UPlotSeriesConfig[] = [{}]

  if (!processDataLayer?.enabled || chartData.length === 0) {
    return { data: [[]], series: seriesConfigs }
  }

  const sensorsToPlot = allSensorsToRender.filter((s) => s.id !== xAxisSensorId)

  if (sensorsToPlot.length === 0) {
    return { data: [[]], series: seriesConfigs }
  }

  const { sensorXValues, sensorYValues } = buildSensorValueMaps(
    sensorsToPlot,
    chartData,
    xAxisSensorId
  )

  const sortedX = buildUnifiedXAxis(sensorsToPlot, sensorXValues)

  if (sortedX.length === 0) {
    return { data: [[]], series: seriesConfigs }
  }

  const sensorLookups = buildSensorLookups(
    sensorsToPlot,
    sensorXValues,
    sensorYValues
  )

  const { yColumns, seriesAdditions } = buildAlignedColumns({
    sensorsToPlot,
    sortedX,
    sensorLookups,
    sensorVisibilityConfig,
    customSensorColors,
    highlightedSensorId,
    secondaryHighlightedSensorIds,
    annotationProtectedSensorIds,
    deemphasizedSensorIds,
    connectPoints,
    enhancedSensorEntries,
    sensorAxisMapping,
    singleScaleMode,
  })

  seriesAdditions.forEach((s) => seriesConfigs.push(s))

  const regressionData = generateRegressionColumns(
    regressionConfigs,
    regressionResults,
    chartData,
    xAxisSensorId ?? undefined,
    sensorAxisMapping,
    allSensorsToRender,
    enhancedSensorEntries,
    derivedValues,
    singleScaleMode,
    sortedX
  )

  regressionData.forEach(({ yColumn, series }) => {
    yColumns.push(yColumn)
    seriesConfigs.push(series)
  })

  const alignedData: uPlot.AlignedData = [sortedX, ...yColumns]
  return { data: alignedData, series: seriesConfigs }
}

interface RegressionColumn {
  yColumn: (number | null | undefined)[]
  series: UPlotSeriesConfig
}

interface RegressionSeriesOptions {
  config: RegressionConfig
  sensorDisplayName: string
  lineColor: string
  scaleKey: string
}

function buildRegressionSeries(
  options: RegressionSeriesOptions
): UPlotSeriesConfig {
  const { config, sensorDisplayName, lineColor, scaleKey } = options
  return {
    label: `${sensorDisplayName} (${config.type} fit)`,
    scale: scaleKey,
    show: true,
    stroke: lineColor,
    width: 1.2,
    dash: [6, 4],
    spanGaps: true,
    points: { show: false },
    _sensorId: config.yVariable,
    _isRegression: true,
  }
}

function buildXAxisRegressionColumn(
  config: RegressionConfig,
  regressionResult: RegressionResult,
  sortedX: number[],
  sensorDisplayName: string,
  lineColor: string,
  scaleKey: string
): RegressionColumn {
  const intercept = regressionResult.coefficients[0]
  const slope = regressionResult.coefficients[1]

  return {
    yColumn: sortedX.map((x) => intercept + slope * x),
    series: buildRegressionSeries({
      config,
      sensorDisplayName,
      lineColor,
      scaleKey,
    }),
  }
}

function buildTimestampRegressionColumn(
  config: RegressionConfig,
  regressionResult: RegressionResult,
  chartData: ChartDataPoint[],
  sortedX: number[],
  sensorDisplayName: string,
  lineColor: string,
  scaleKey: string
): RegressionColumn | null {
  const dataTimestamps = chartData
    .map((d) => d.timestamp)
    .filter((t): t is number => t !== null)

  if (dataTimestamps.length === 0) return null

  const minDataTime = Math.min(...dataTimestamps)
  const maxDataTime = Math.max(...dataTimestamps)
  const endTime = config.endDate
    ? new Date(config.endDate).getTime()
    : maxDataTime

  const numPoints = DEFAULT_CHART_SETTINGS.NUM_REGRESSION_POINTS
  const regressionTimestamps: number[] = []
  for (let i = 0; i <= numPoints; i++) {
    regressionTimestamps.push(
      minDataTime + (endTime - minDataTime) * (i / numPoints)
    )
  }

  const regressionValues = predictValues(
    regressionResult,
    config,
    regressionTimestamps
  )

  // Regression is computed over its own timestamp grid and must be interpolated
  // onto the unified x-axis since that axis cannot be modified after the fact.
  const yColumn: (number | null | undefined)[] = sortedX.map((x) => {
    if (x < minDataTime || x > endTime) return null

    let leftIdx = -1
    for (let i = regressionTimestamps.length - 1; i >= 0; i--) {
      if (regressionTimestamps[i] <= x) {
        leftIdx = i
        break
      }
    }

    if (leftIdx === -1) return regressionValues[0]
    if (leftIdx >= regressionTimestamps.length - 1)
      return regressionValues[regressionValues.length - 1]

    const t1 = regressionTimestamps[leftIdx]
    const t2 = regressionTimestamps[leftIdx + 1]
    const v1 = regressionValues[leftIdx]
    const v2 = regressionValues[leftIdx + 1]

    if (v1 === null || v2 === null) return null
    if (t2 === t1) return v1

    const ratio = (x - t1) / (t2 - t1)
    return v1 + ratio * (v2 - v1)
  })

  return {
    yColumn,
    series: buildRegressionSeries({
      config,
      sensorDisplayName,
      lineColor,
      scaleKey,
    }),
  }
}

function generateRegressionColumns(
  regressionConfigs: RegressionConfig[],
  regressionResults: Record<string, RegressionResult>,
  chartData: ChartDataPoint[],
  xAxisSensorId: string | undefined,
  sensorAxisMapping: SensorAxisMapping,
  allSensorsToRender: Array<{ id: string; name: string }>,
  enhancedSensorEntries: EnhancedSensorEntry[],
  derivedValues: TimeSeriesDerivedValueEntryJSON[],
  singleScaleMode: boolean,
  sortedX: number[]
): RegressionColumn[] {
  const result: RegressionColumn[] = []

  regressionConfigs.forEach((config) => {
    if (!config.enabled || !regressionResults[config.id]) return

    const regressionResult = regressionResults[config.id]
    const isDerivedRegression = config.yVariable.startsWith('derived_')
    let sensorDisplayName: string

    if (isDerivedRegression) {
      const derivedValue = derivedValues.find(
        (dv) => dv.id === config.yVariable
      )
      sensorDisplayName = derivedValue?.data.name || config.yVariable
    } else {
      const sensor = enhancedSensorEntries.find(
        (e) => e.id === config.yVariable
      )
      sensorDisplayName = sensor?.data?.name || config.yVariable
    }

    const sensorIndex = allSensorsToRender.findIndex(
      (s) => s.id === config.yVariable
    )
    if (sensorIndex === -1) return

    const lineColor = getSensorColor(sensorIndex)
    const scaleKey = singleScaleMode
      ? 'y'
      : sensorAxisMapping[config.yVariable]
        ? config.yVariable
        : 'y'

    if (
      xAxisSensorId &&
      config.regressionType === 'linear' &&
      config.xVariables?.length === 1 &&
      config.xVariables[0] === xAxisSensorId
    ) {
      result.push(
        buildXAxisRegressionColumn(
          config,
          regressionResult,
          sortedX,
          sensorDisplayName,
          lineColor,
          scaleKey
        )
      )
      return
    }

    if (!xAxisSensorId && config.xVariables[0] === 'timestamp') {
      const column = buildTimestampRegressionColumn(
        config,
        regressionResult,
        chartData,
        sortedX,
        sensorDisplayName,
        lineColor,
        scaleKey
      )
      if (column) result.push(column)
    }
  })

  return result
}
